/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  RemoteParticipant,
  Room,
  RoomConnectOptions,
  RoomEvent,
  // ConnectionState,
} from "livekit-client";

interface UseLivekitRoomOpts {
  wsUrl: string;
  token?: string;
}

function validateWsUrl(url: string) {
  try {
    const u = new URL(url);
    if (u.protocol !== "ws:" && u.protocol !== "wss:") {
      throw new Error(
        `LiveKit URL must use ws:// or wss:// (got ${u.protocol})`
      );
    }
  } catch (e) {
    throw new Error(`Invalid LiveKit URL: ${(e as Error).message}`);
  }
}

export function useLivekitRoom({ wsUrl, token }: UseLivekitRoomOpts) {
  const [room, setRoom] = useState<Room>();
  const [remotes, setRemotes] = useState<RemoteParticipant[]>([]);
  const [isJoining, setIsJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const roomRef = useRef<Room | null>(null);

  const syncRemotes = useCallback((lkRoom: Room) => {
    setRemotes(Array.from(lkRoom.remoteParticipants.values()));
  }, []);

  const join = useCallback(async () => {
    // console.log("[LiveKit] Starting connection attempt", {
    //   wsUrl,
    //   hasToken: !!token,
    //   tokenLength: token?.length || 0,
    //   timestamp: new Date().toISOString(),
    // });

    setError(null);

    const raw = token ?? "";
    const trimmedToken = raw.trim();
    if (!trimmedToken) {
      // console.error("[LiveKit] Missing token - cannot connect");
      setError("Missing LiveKit token");
      return;
    }

    try {
      validateWsUrl(wsUrl);
      // console.log("[LiveKit] WebSocket URL validated:", wsUrl);
    } catch (e) {
      // console.error("[LiveKit] Invalid WebSocket URL:", (e as Error).message);
      setError((e as Error).message);
      return;
    }

    setIsJoining(true);
    // console.log("[LiveKit] Creating Room instance...");

    const lkRoom = new Room({
      adaptiveStream: true,
      dynacast: true,
      stopLocalTrackOnUnpublish: true,
    });

    roomRef.current = lkRoom;
    // console.log("[LiveKit] Room instance created");

    const handleRemotes = () => {
      // const remoteCount = Array.from(lkRoom.remoteParticipants.values()).length;
      // console.log("[LiveKit] Remote participants updated:", remoteCount);
      syncRemotes(lkRoom);
    };

    lkRoom.on(RoomEvent.ParticipantConnected, () => {
      // console.log("[LiveKit] Participant connected:", participant.identity);
      handleRemotes();
    });

    lkRoom.on(RoomEvent.ParticipantDisconnected, () => {
      // console.log("[LiveKit] Participant disconnected:", participant.identity);
      handleRemotes();
    });

    // lkRoom.on(RoomEvent.ConnectionStateChanged, (state: ConnectionState) => {
    //   // console.log("[LiveKit] Connection state changed:", {
    //   //   state,
    //   //   reason: reason?.message || reason?.reasonName,
    //   //   timestamp: new Date().toISOString(),
    //   // });
    //   // if (state === "connected") {
    //   //   console.log("✅ CONNECTED SUCCESSFULLY!");
    //   // }
    // });

    lkRoom.on(RoomEvent.Disconnected, (reason?: any) => {
      // console.warn("[LiveKit] Disconnected event:", {
      //   reason: reason?.message || reason?.reasonName || "unknown",
      //   timestamp: new Date().toISOString(),
      // });

      if (roomRef.current) {
        if (reason?.status === 401) {
          setError("Authorization failed (401): invalid or expired token.");
        } else if (reason?.message) {
          setError(reason.message);
        }
      }

      setRoom(undefined);
      setRemotes([]);
    });

    try {
      // console.log("[LiveKit] Attempting to connect...");

      const connectionOptions: RoomConnectOptions = {
        autoSubscribe: true,
      };

      await lkRoom.connect(wsUrl, trimmedToken, connectionOptions);

      // console.log("[LiveKit] ✅ Successfully connected!", {
      //   roomName: lkRoom.name,
      //   localParticipant: lkRoom.localParticipant?.identity,
      //   timestamp: new Date().toISOString(),
      // });

      setRoom(lkRoom);
      syncRemotes(lkRoom);
    } catch (err: any) {
      // console.error("[LiveKit] Connection failed:", err?.message || err);

      if (err?.status === 401) {
        setError("Authorization failed (401): invalid or expired token.");
      } else if (err?.message) {
        setError(err.message);
      } else {
        setError("LiveKit connection failed.");
      }

      lkRoom.disconnect();
      roomRef.current = null;

      throw err;
    } finally {
      setIsJoining(false);
      // console.log("[LiveKit] Connection attempt finished");
    }
  }, [token, wsUrl, syncRemotes]);

  const disconnect = useCallback(() => {
    // console.log("[LiveKit] Disconnecting...");
    roomRef.current?.disconnect();
    roomRef.current = null;
    setRoom(undefined);
    setRemotes([]);
    setError(null);
  }, []);

  useEffect(() => {
    return () => {
      roomRef.current?.disconnect();
      roomRef.current = null;
    };
  }, []);

  return useMemo(
    () =>
      ({
        room,
        remotes,
        join,
        disconnect,
        isJoining,
        error,
      }) as const,
    [room, remotes, join, disconnect, isJoining, error]
  );
}
